import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Compact language switcher for the (pre-auth) login surfaces.
 *
 * `langs` is the allow-list to display — pass the tenant's enabled_languages so
 * the choices reflect the super_admin config (e.g. adding 'fr' makes it appear).
 * Defaults to all locales when omitted. Uses react-i18next + flips dir for AR.
 */
const LANG_LABELS = {
  fr: { native: 'Français', flag: '🇫🇷', short: 'FR' },
  en: { native: 'English', flag: '🇬🇧', short: 'EN' },
  it: { native: 'Italiano', flag: '🇮🇹', short: 'IT' },
  de: { native: 'Deutsch', flag: '🇩🇪', short: 'DE' },
  ar: { native: 'العربية', flag: '🇸🇦', short: 'AR' },
};
const ALL_LANGS = ['fr', 'en', 'it', 'de', 'ar'];

const LangSwitcher = ({ langs = ALL_LANGS }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const options = (langs?.length ? langs : ALL_LANGS).filter((c) => LANG_LABELS[c]);
  const base = (i18n.language || 'en').split('-')[0];
  const current = options.includes(base) ? base : options[0];

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const pick = (code) => {
    i18n.changeLanguage(code);
    document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = code;
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-foreground/5 transition-colors"
      >
        <span className="text-sm leading-none">{LANG_LABELS[current]?.flag}</span>
        <span>{LANG_LABELS[current]?.short}</span>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute end-0 mt-2 w-40 rounded-xl border border-border bg-card shadow-elevation-2 p-1 z-50"
        >
          {options.map((code) => {
            const isActive = code === current;
            return (
              <button
                key={code}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => pick(code)}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-start transition-colors ${
                  isActive ? 'bg-foreground/5 text-foreground font-semibold' : 'text-muted-foreground hover:bg-foreground/5'
                }`}
              >
                <span className="text-base leading-none">{LANG_LABELS[code]?.flag}</span>
                <span className="flex-1" dir="auto">{LANG_LABELS[code]?.native}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LangSwitcher;
